import { CreateAnalyticsPageApiResponse } from "../../types/analytics/admin/CreateAnalyticsPageApiResponse";
import { CreateAnalyticsPageFormData } from "../../types/analytics/admin/CreateAnalyticsPageFormData";
import { CreateAnalyticsSimpleWidgetApiResponse } from "../../types/analytics/admin/CreateAnalyticsSimpleWidgetApiResponse";
import { CreateAnalyticsSimpleWidgetFormData } from "../../types/analytics/admin/CreateAnalyticsSimpleWidgetFormData";
import { GetWidgetsForPageApiResponse } from "../../types/analytics/admin/GetWidgetsForPageApiResponse";
import {
  AnalyticsDataPointMatchResponse,
  AnalyticsPageAdminDto,
  AnalyticsWidgetAdminDto,
  UpdateAnalyticsPageFormData,
  UpdateAnalyticsWidgetFormData,
} from "../../types/analytics/admin/AnalyticsAdminModels";
import type { AnalyticsNavItem } from "../../types/dtos/users/SignedInUserDto";
import type {
  AnalyticsClientFormOptionDto,
  ConfirmSimpleWidgetAiItem,
  ConfirmSimpleWidgetAiSuggestionsResponse,
  SimpleWidgetAiSuggestionsResponse,
} from "../../types/analytics/admin/SimpleWidgetAiSuggestions";
import { apiClient } from "../apiClient";

class analyticsAdminApi {
  constructor(authToken: string | undefined) {
    this.accessToken = authToken;
  }

  accessToken: string | undefined;

  addNewPage(
    model: CreateAnalyticsPageFormData,
    onSuccess: (data: CreateAnalyticsPageApiResponse) => void,
    onError: (error: any) => void
  ) {
    return apiClient(
      this.accessToken,
      "analytics/analytics-admin/pages",
      {
        body: JSON.stringify(model),
      }
    ).then(
      (data) => {
        const responseData = data as CreateAnalyticsPageApiResponse;
        onSuccess(responseData);
      },
      (error) => {
        onError(error);
      }
    );
  }

  addNewSimpleWidget(
    model: CreateAnalyticsSimpleWidgetFormData,
    onSuccess: (data: CreateAnalyticsSimpleWidgetApiResponse) => void,
    onError: (error: any) => void
  ) {
    return apiClient(
      this.accessToken,
      `analytics/analytics-admin/pages/${model.pageId}/widgets`,
      {
        body: JSON.stringify(model),
      }
    ).then(
      (data) => {
        const responseData = data as CreateAnalyticsSimpleWidgetApiResponse;
        onSuccess(responseData);
      },
      (error) => {
        onError(error);
      }
    );
  }

  getWidgetsForPage(
    pageId: number,
    onSuccess: (data: GetWidgetsForPageApiResponse) => void,
    onError: (error: any) => void
  ) {
    return apiClient(
      this.accessToken,
      `analytics/analytics-admin/pages/${pageId}/widgets`,
      {
        method: "GET",
      }
    ).then(
      (data) => {
        onSuccess(data as GetWidgetsForPageApiResponse);
      },
      (error) => {
        onError(error);
      }
    );
  }

  getPages(
    onSuccess: (data: AnalyticsPageAdminDto[]) => void,
    onError: (error: any) => void
  ) {
    return apiClient(this.accessToken, "analytics/analytics-admin/pages", {
      method: "GET",
    }).then(
      (data) => {
        onSuccess(data as AnalyticsPageAdminDto[]);
      },
      (error) => {
        onError(error);
      }
    );
  }

  getPage(
    pageId: number,
    onSuccess: (data: AnalyticsPageAdminDto) => void,
    onError: (error: any) => void
  ) {
    return apiClient(
      this.accessToken,
      `analytics/analytics-admin/pages/${pageId}`,
      { method: "GET" }
    ).then(
      (data) => onSuccess(data as AnalyticsPageAdminDto),
      (error) => onError(error)
    );
  }

  getNavigationPages(
    onSuccess: (data: AnalyticsNavItem[]) => void,
    onError: (error: any) => void
  ) {
    return apiClient(
      this.accessToken,
      "analytics/analytics-admin/navigation-pages",
      { method: "GET" }
    ).then(
      (data) => onSuccess(data as AnalyticsNavItem[]),
      (error) => onError(error)
    );
  }

  updatePage(
    model: UpdateAnalyticsPageFormData,
    onSuccess: () => void,
    onError: (error: any) => void
  ) {
    return apiClient(this.accessToken, `analytics/analytics-admin/pages/${model.pageId}`, {
      method: "PUT",
      body: JSON.stringify(model),
    }).then(onSuccess, onError);
  }

  deletePage(
    pageId: number,
    onSuccess: () => void,
    onError: (error: any) => void
  ) {
    return apiClient(
      this.accessToken,
      `analytics/analytics-admin/pages/${pageId}`,
      { method: "DELETE" }
    ).then(onSuccess, onError);
  }

  findMatchingDataPoints(
    clientFormId: number,
    scoreRef: string,
    onSuccess: (data: AnalyticsDataPointMatchResponse) => void,
    onError: (error: any) => void
  ) {
    const query = new URLSearchParams({
      clientFormId: String(clientFormId),
      scoreRef,
    });
    return apiClient(
      this.accessToken,
      `analytics/analytics-admin/datapoints?${query.toString()}`,
      { method: "GET" }
    ).then(
      (data) => onSuccess(data as AnalyticsDataPointMatchResponse),
      (error) => onError(error)
    );
  }

  getWidget(
    widgetId: number,
    onSuccess: (data: AnalyticsWidgetAdminDto) => void,
    onError: (error: any) => void
  ) {
    return apiClient(
      this.accessToken,
      `analytics/analytics-admin/widgets/${widgetId}`,
      { method: "GET" }
    ).then(
      (data) => onSuccess(data as AnalyticsWidgetAdminDto),
      (error) => onError(error)
    );
  }

  updateWidget(
    model: UpdateAnalyticsWidgetFormData,
    onSuccess: () => void,
    onError: (error: any) => void
  ) {
    return apiClient(
      this.accessToken,
      `analytics/analytics-admin/widgets/${model.widgetId}`,
      { method: "PUT", body: JSON.stringify(model) }
    ).then(onSuccess, onError);
  }

  deleteWidget(
    widgetId: number,
    onSuccess: () => void,
    onError: (error: any) => void
  ) {
    return apiClient(
      this.accessToken,
      `analytics/analytics-admin/widgets/${widgetId}`,
      { method: "DELETE" }
    ).then(onSuccess, onError);
  }

  getClassicForms(
    onSuccess: (data: AnalyticsClientFormOptionDto[]) => void,
    onError: (error: any) => void
  ) {
    return apiClient(
      this.accessToken,
      "analytics/analytics-admin/classic-forms",
      { method: "GET" }
    ).then(
      (data) => onSuccess(data as AnalyticsClientFormOptionDto[]),
      (error) => onError(error)
    );
  }

  generateSimpleWidgetSuggestions(
    pageId: number,
    clientFormId: number,
    onSuccess: (data: SimpleWidgetAiSuggestionsResponse) => void,
    onError: (error: any) => void
  ) {
    return apiClient(
      this.accessToken,
      `analytics/analytics-admin/pages/${pageId}/widgets/ai-suggestions`,
      {
        body: JSON.stringify({ clientFormId }),
      }
    ).then(
      (data) => onSuccess(data as SimpleWidgetAiSuggestionsResponse),
      (error) => onError(error)
    );
  }

  confirmSimpleWidgetSuggestions(
    pageId: number,
    widgets: ConfirmSimpleWidgetAiItem[],
    onSuccess: (data: ConfirmSimpleWidgetAiSuggestionsResponse) => void,
    onError: (error: any) => void
  ) {
    return apiClient(
      this.accessToken,
      `analytics/analytics-admin/pages/${pageId}/widgets/ai-suggestions/confirm`,
      {
        body: JSON.stringify({ widgets }),
      }
    ).then(
      (data) => onSuccess(data as ConfirmSimpleWidgetAiSuggestionsResponse),
      (error) => onError(error)
    );
  }
}

export default analyticsAdminApi;
